import { defineStore } from "pinia"

import * as sessionApi from "@/api/sessionApi.js"
import * as startupApi from "@/api/startupApi.js"
import * as activitiesApi from "@/api/activitiesApi.js"
import * as accountsApi from "@/api/accountsApi.js"
import * as paywallApi from "@/api/paywallApi.js"

import * as sentryReporter from "@/services/sentryReporter.js"
import * as paywallService from "@/services/paywallService.js"

import { isFuture, isPast, differenceInMonths } from "date-fns"

import { intervalPeriods } from "@/constants/paywall.js"

import { useConfigurationStore } from "@/store/configuration.js"

export const useUserSessionStore = defineStore("userSession", {
  state: () => ({
    sessionState: null,
    userDataState: null,
    loggingInState: false,
    magicLinkRequestDateState: null,
    ownedWebSubscriptionsState: null,
    marketingPreferencesState: {
      global_opt_in: null,
      digest_email: false,
      global_opt_in_can_ask: false,
      global_opt_in_can_ask_modal: false
    },
    paywallDataState: null,
    userLogoUrl: null
  }),
  getters: {
    session: (state) => state.sessionState,
    sessionQueryString(state) {
      if (!state.sessionState) {
        return null
      }
      return `X-Secret-Id=${state.sessionState.secret_id}&X-Secret=${state.sessionState.secret}`
    },
    user: (state) => state.userDataState?.user,
    emailConfirmationRequired: (state) => state.userDataState?.user?.email_confirmation_required && !state.userDataState?.user?.email_confirmed,
    walkthroughCompleted: (state) => state.userDataState?.walkthrough_completed,
    walkthroughLastSeenSlideId: (state) => state.userDataState?.last_walkthrough_step?.walkthrough_step.id,
    paywallData: (state) => state.paywallDataState,
    ownedPaywallPlan: (state) => {
      const ownedPlanPurchase = state.userDataState?.owned_subscriptions?.[0]

      if (!ownedPlanPurchase) {
        return null
      }

      const relatedPaywallPlan = state.paywallDataState?.plans.find((p) => p.name === ownedPlanPurchase.subscription_name)
      const interval_period =
        differenceInMonths(new Date(ownedPlanPurchase.expires_at), new Date(ownedPlanPurchase.started_at)) < 11 ? intervalPeriods.MONTH : intervalPeriods.YEAR

      return {
        ...ownedPlanPurchase,
        title: relatedPaywallPlan?.title,
        icon_url: relatedPaywallPlan?.icon_url,
        interval_period,
        price_in_cents: relatedPaywallPlan?.web_subscriptions.find((ws) => ws.interval_period === interval_period)?.price_in_cents
      }
    },
    webSubscriptions: (state) =>
      (state.userDataState?.owned_web_subscriptions || []).map((s) => {
        const relatedPlan = state.paywallDataState?.plans.find((t) => t.web_subscriptions.find((ws) => ws.id === s.web_subscription.id))
        return {
          ...s,
          activeCancelled: Boolean(s.cancelled_at && isFuture(new Date(s.current_period_end))),
          planName: relatedPlan?.name,
          planTitle: relatedPlan?.title,
          pastDue: Boolean(!s.cancelled_at && isPast(new Date(s.current_period_end)))
        }
      }),
    ownedWebSubscriptionPurchase() {
      return this.webSubscriptions.find((s) => !s.ended_at) || this.webSubscriptions.find((s) => s.activeCancelled)
    },
    pastDueWebSubscriptionPurchase() {
      return this.webSubscriptions.find((s) => s.pastDue)
    },
    newActivitiesCount: (state) => state.userDataState?.new_activities_total_count,
    newMessagesCount: (state) => state.userDataState?.unread_messages_count,
    isLoggedIn: (state) => state.sessionState !== null && state.userDataState !== null,
    canStream: (state) => state.userDataState?.user?.role === "artist",
    isLoggingIn: (state) => state.loggingInState,
    displayName: (state) => state.userDataState?.user?.display_name,
    magicLinkRequestDate: (state) => state.magicLinkRequestDateState,
    messagingUpdatesChannel: (state) => state.userDataState?.updates_channel,
    marketingPreferences: (state) => state.marketingPreferencesState,
    privacyPolicyOutdated: (state) => {
      const configurationStore = useConfigurationStore()
      return Boolean(
        state.userDataState?.user && configurationStore.configuration.privacyPolicyVersion > state.userDataState.user.agreed_legal.privacy_policy_version
      )
    },
    termsAndConditionsOutdated: (state) => {
      const configurationStore = useConfigurationStore()
      return Boolean(
        state.userDataState?.user && configurationStore.configuration.termsAndConditionsVersion > state.userDataState.user.agreed_legal.terms_conditions_version
      )
    },
    ownedProducts: (state) => state.userDataState?.owned_products,
    manageUiDisplayed: (state) => state.userDataState?.user?.manage_ui_displayed,
    logoUrl: (state) => {
      const configurationStore = useConfigurationStore()
      return state.userLogoUrl || configurationStore.configuration.appIconUrl
    }
  },
  actions: {
    async logIn({ credentials }) {
      try {
        this.loggingInState = true

        const data = await sessionApi.logIn(credentials)
        await this.setSessionAndUserData(data.authentication_token)

        this.loggingInState = false
      } catch (err) {
        this.clearSession()
        throw err
      }
    },
    async openIdLogIn({ accessToken }) {
      try {
        this.loggingInState = true

        const data = await sessionApi.openIdLogIn({ accessToken })
        await this.setSessionAndUserData(data.authentication_token)

        this.loggingInState = false
      } catch (err) {
        this.clearSession()
        throw err
      }
    },
    async samlLogIn(data) {
      try {
        this.loggingInState = true

        await this.setSessionAndUserData(data.authentication_token)

        this.loggingInState = false
      } catch (err) {
        this.clearSession()
        throw err
      }
    },
    async requestMagicLink({ email, code }) {
      try {
        await sessionApi.requestMagicLink({ email, code })
        this.magicLinkRequestDateState = new Date().toISOString()
      } catch (err) {
        this.magicLinkRequestDateState = null
        throw err
      }
    },
    async logOut() {
      try {
        await sessionApi.logOut()
      } catch (err) {
        sentryReporter.reportError(err)
      } finally {
        this.clearSession()
      }
    },
    clearSession() {
      this.sessionState = null
      this.userDataState = null
      this.loggingInState = false
      this.userLogoUrl = null
      this.marketingPreferencesState = { global_opt_in: null, digest_email: false }
    },
    setSessionData(session) {
      this.sessionState = session
    },
    async setSessionAndUserData(session) {
      this.setSessionData(session)
      if (!this.userDataState) {
        await this.setUserData()
      }
    },
    async setProfileHeaderLogo() {
      try {
        const result = await accountsApi.profileFieldHeaderLogo()

        if (!result) {
          return
        }

        this.userLogoUrl = result["100x100"] || result[Object.keys(result)[0]]
      } catch (err) {
        sentryReporter.reportError(err)
      }
    },
    async setUserData({ throwable } = {}) {
      if (!this.sessionState) {
        return
      }

      try {
        const data = await startupApi.getStartupData()
        this.userDataState = data

        const configurationStore = useConfigurationStore()

        if (configurationStore.configuration.profileFieldHeaderLogoEnabled && data.user.onboarding_completed) {
          this.setProfileHeaderLogo()
        }

        //Temporary trying to find out what is happening with this error:
        if (!data) {
          sentryReporter.reportError(new Error("Empty User Data"))
        }
        if (!data.user) {
          sentryReporter.reportError(new Error("Empty User"))
        }

        if (configurationStore.configuration.emailNotificationsEnabled) {
          await this.fetchMarketingPreferences()
        }
      } catch (err) {
        // Do not clear session when there is a network error or server is down
        if (err?.response && err.response.status !== 500) {
          this.clearSession()
        }
        if (throwable) {
          //For further handling
          throw err
        }
      }
    },
    async resetCounters() {
      try {
        const data = await startupApi.getStartupData()
        this.setCounters(data)
      } catch (err) {
        sentryReporter.reportError(err)
      }
    },
    async refreshSession() {
      try {
        if (!this.sessionState) {
          return
        }

        const data = await sessionApi.refresh(this.sessionState)
        await this.setSessionAndUserData(data.authentication_token)
      } catch (err) {
        if (err?.response && err.response.status !== 500) {
          this.clearSession()
          throw err
        }
      }
    },
    async resetUnreadActivitiesCounter() {
      this.userDataState.new_activities_total_count = 0
      try {
        await activitiesApi.resetUnreadActivitiesCounter()
      } catch (err) {
        sentryReporter.reportError(err)
      }
    },
    setCounters({ new_activities_total_count, new_activities_count, unread_messages_count }) {
      //Startup endpoint vs pubnub event
      this.userDataState.new_activities_total_count = new_activities_total_count || new_activities_count
      this.userDataState.unread_messages_count = unread_messages_count
    },
    async fetchMarketingPreferences() {
      const { marketing_preferences } = await accountsApi.marketingPreferences()

      if (!marketing_preferences) {
        sentryReporter.reportError(new Error("Empty Marketing Preferences"))
      }

      this.marketingPreferencesState = marketing_preferences
    },
    async setMarketingPreferences(value) {
      const { marketing_preferences } = await accountsApi.updateMarketingPreferences(value)
      this.marketingPreferencesState = marketing_preferences
    },
    async fetchPaywallData() {
      try {
        const paywallData = await paywallApi.paywallData()

        const processedPaywallData = paywallService.getPaywallDataWithUniformBillingPeriods(paywallData)

        paywallService.applyApiPaywallDecriptionStyles(processedPaywallData)
        paywallService.applyApiPaywallPlanStyles(processedPaywallData)

        this.paywallDataState = processedPaywallData
      } catch (err) {
        sentryReporter.reportError(err)
      }
    }
  },
  persist: {
    pick: ["sessionState", "magicLinkRequestDateState"]
  }
})
